/**
 * Bracket schema helpers.
 *
 * The legacy schema stored brackets as an array: `bracket: [match1, match2, ...]`.
 * The new schema (SCHEMA_VERSION = 2) stores brackets as a map keyed by match ID
 * plus a parallel ordering array: `bracketMap: { [matchId]: match }`, `matchOrder: [matchId]`.
 *
 * The map shape enables Firestore field-path updates like
 *   updateDoc(ref, { 'bracketMap.m5.score1': 10 })
 * which sends only the changed bytes instead of rewriting the entire bracket array.
 * Dot-notation paths into array indices are not supported by Firestore — that's
 * the reason for the schema change.
 *
 * Render code continues to consume a bracket *array*, so conversion happens at
 * the I/O boundary (load from Firestore, write back to Firestore).
 */

import { deleteField } from 'firebase/firestore';

export const SCHEMA_VERSION = 2;

/**
 * Reduce a single participant (p1/p2) to its lean persisted shape.
 *
 * buildBracket() attaches a recursive `sourceMatch` blob to p1/p2 — a build-time
 * artifact used only to compute each match's `from` line-routing, never read by
 * the renderer or propagation. Because each sourceMatch is a full match whose own
 * p1/p2 carry their own sourceMatch, losers-bracket participants nest exponentially
 * and bloat both in-memory state and the Firestore document. We keep only
 * `{ name, ref }` (or `{ name }` when there is no ref); `from` already lives on the
 * match itself, so connector rendering is unaffected.
 */
export function stripParticipant(part) {
  if (!part || typeof part !== 'object') return part;
  return part.ref !== undefined ? { name: part.name, ref: part.ref } : { name: part.name };
}

/**
 * Return true if any match participant in the bracket still carries the recursive
 * `sourceMatch` build artifact. Used to detect bloated (pre-fix) documents so they
 * can be de-bloated and re-saved once on load.
 */
export function bracketHasSourceMatchBloat(bracket) {
  if (!Array.isArray(bracket)) return false;
  return bracket.some(
    (m) => (m?.p1 && m.p1.sourceMatch !== undefined) || (m?.p2 && m.p2.sourceMatch !== undefined)
  );
}

/**
 * Strip the recursive `sourceMatch` blob from every match's p1/p2, returning a new
 * bracket array of lean matches. Mutates participant references to the lean shape
 * but preserves every other match field (scores, winner, from, timestamps, etc.).
 */
export function stripSourceMatch(bracket) {
  if (!Array.isArray(bracket)) return bracket;
  return bracket.map((m) => {
    if (!m || typeof m !== 'object') return m;
    const next = { ...m };
    if (next.p1 && typeof next.p1 === 'object') next.p1 = stripParticipant(next.p1);
    if (next.p2 && typeof next.p2 === 'object') next.p2 = stripParticipant(next.p2);
    return next;
  });
}

/**
 * Extract the distinct real participant names from a bracket, in first-appearance
 * order. Excludes BYE seats and unresolved "Winner of …"/"Loser of …" placeholders.
 *
 * The bracket is the source of truth for who is actually playing. The separately
 * maintained player-list text can drift from it (e.g. a late player swapped into a
 * BYE slot never gets written back), so persistence derives the saved players list
 * from here instead of from that text.
 */
export function getBracketPlayers(bracket) {
  if (!Array.isArray(bracket) || bracket.length === 0) return [];
  const names = [];
  const seen = new Set();
  for (const match of bracket) {
    for (const slot of ['p1', 'p2']) {
      const name = match?.[slot]?.name;
      if (
        typeof name === 'string' &&
        name &&
        name !== 'BYE' &&
        !name.startsWith('Winner of') &&
        !name.startsWith('Loser of') &&
        !seen.has(name)
      ) {
        seen.add(name);
        names.push(name);
      }
    }
  }
  return names;
}

/**
 * Convert a bracket array into the map-and-order representation used by SCHEMA_VERSION 2.
 *
 * Returns `{ bracketMap, matchOrder }`. If a match lacks an `id`, a stable
 * positional fallback (`m-${index}`) is assigned and written into the match
 * object so subsequent diffs are stable.
 */
export function bracketArrayToMap(bracketArray) {
  if (!Array.isArray(bracketArray) || bracketArray.length === 0) {
    return { bracketMap: {}, matchOrder: [] };
  }
  const bracketMap = {};
  const matchOrder = [];
  for (let i = 0; i < bracketArray.length; i++) {
    const original = bracketArray[i];
    if (!original || typeof original !== 'object') continue;
    const id = original.id != null ? String(original.id) : `m-${i}`;
    const match = original.id != null ? original : { ...original, id };
    bracketMap[id] = match;
    matchOrder.push(id);
  }
  return { bracketMap, matchOrder };
}

/**
 * Read the bracket as an array from a tournament document or instance object,
 * preferring the new `bracketMap` + `matchOrder` schema and falling back to a
 * legacy `bracket` array. Existence of this helper is the temporary bridge for
 * the migration window — once every tournament is at SCHEMA_VERSION the
 * fallback can be removed but the helper itself stays as the read-boundary
 * conversion point.
 */
export function readBracketAsArray(source) {
  if (!source) return [];
  if (source.bracketMap && typeof source.bracketMap === 'object') {
    return bracketMapToArray(source.bracketMap, source.matchOrder);
  }
  if (Array.isArray(source.bracket)) return source.bracket;
  return [];
}

/**
 * Materialize a bracket array from the map-and-order representation.
 *
 * Honors `matchOrder` for display sequence. Falls back to the natural key order
 * of `bracketMap` if `matchOrder` is missing or malformed (defensive — should
 * not happen on properly-migrated documents).
 */
export function bracketMapToArray(bracketMap, matchOrder) {
  if (!bracketMap || typeof bracketMap !== 'object') return [];
  if (Array.isArray(matchOrder) && matchOrder.length > 0) {
    const seen = new Set();
    const ordered = [];
    for (const id of matchOrder) {
      const match = bracketMap[id];
      if (match && !seen.has(id)) {
        ordered.push(match);
        seen.add(id);
      }
    }
    // Append any orphan map entries not in matchOrder so we never silently drop matches.
    for (const id of Object.keys(bracketMap)) {
      if (!seen.has(id)) ordered.push(bracketMap[id]);
    }
    return ordered;
  }
  return Object.keys(bracketMap).map(id => bracketMap[id]);
}

/**
 * Build a Firestore field-path update payload that captures the per-match
 * differences between two bracket maps.
 *
 * - For matches whose existing field values changed: emits one entry per
 *   changed field, e.g. `{ '${pathPrefix}.m5.score1': 10 }`.
 * - For matches that are new in `nextMap`: emits the whole match object under
 *   `${pathPrefix}.${matchId}`.
 * - For matches present only in `prevMap`: emits `deleteField()` for that key.
 *
 * The returned object can be spread directly into an `updateDoc` call.
 */
export function diffBracketMaps(prevMap, nextMap, pathPrefix) {
  const updates = {};
  const prev = prevMap || {};
  const next = nextMap || {};

  for (const id of Object.keys(next)) {
    const nextMatch = next[id];
    const prevMatch = prev[id];
    if (!prevMatch) {
      updates[`${pathPrefix}.${id}`] = nextMatch;
      continue;
    }
    if (nextMatch === prevMatch) continue;
    const changedFields = collectChangedFields(prevMatch, nextMatch);
    for (const field of changedFields) {
      updates[`${pathPrefix}.${id}.${field}`] = nextMatch[field];
    }
  }

  for (const id of Object.keys(prev)) {
    if (!(id in next)) {
      updates[`${pathPrefix}.${id}`] = deleteField();
    }
  }

  return updates;
}

function collectChangedFields(prevMatch, nextMatch) {
  const fields = new Set([...Object.keys(prevMatch || {}), ...Object.keys(nextMatch || {})]);
  const changed = [];
  for (const field of fields) {
    if (!valuesEqual(prevMatch?.[field], nextMatch?.[field])) {
      changed.push(field);
    }
  }
  return changed;
}

function valuesEqual(a, b) {
  if (Object.is(a, b)) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!valuesEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if (!valuesEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
}
